/*
 * The privacy policy (draft 14; the old privacy.php, 2026-10-19): one policy for The Trades LLC and its brands, at /privacy
 * on every address, linked under sign in and from the brands' forms. It says only what is true. Change the date when
 * the words change. ?brand=<name> puts that brand's name on top.
 */
import type { ReactNode } from "react";

export const PRIVACY_DATE = "October 28, 2026";

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

export function Privacy() {
  const brand = new URLSearchParams(window.location.search).get("brand")?.slice(0, 60) ?? null;
  return (
    <div className="public">
      <div className="public-band" />
      <main className="public-main privacy">
        {brand ? <p className="public-brand">{brand}</p> : null}
        <h1>Privacy policy</h1>
        <p className="muted">The Steward, by The Trades LLC. Last updated {PRIVACY_DATE}.</p>
        <p>
          This explains what we collect when you book with us, pay us, or work with us, and what we do with it. It covers The
          Trades and its brands, The Cleaners, The Washers, The GroundsMen and The Marketers.
        </p>
        <Section title="What we collect">
          <ul>
            <li>Your name, phone, email and the address where we do the work.</li>
            <li>What we do for you, when, and what you paid. Card numbers are kept by Stripe, never by us.</li>
            <li>Messages you send us by text or email.</li>
            <li>For our team, what payroll and the law need.</li>
          </ul>
        </Section>
        <Section title="How we use it">
          <ul>
            <li>To schedule and do the work, send quotes, invoices and reminders, take payments, and pay our team.</li>
            <li>To tell you about services from our other brands. You can opt out at any time, as explained below.</li>
            <li>Bank data is used only to keep the business&apos;s own books.</li>
          </ul>
        </Section>
        <Section title="Who we share it with">
          <ul>
            <li>
              <b>Our family of brands.</b> Customer information is shared across The Trades and its brands so we can serve you and
              tell you about our other services.
            </li>
            <li>
              <b>Our franchise locations.</b> Each franchise location is independently owned and operated. A location gets what it
              needs to serve you.
            </li>
            <li>
              <b>Companies that help us run the business.</b> Only what each needs. That means Stripe (payments), Plaid (bank
              connections), Quo (texts), Google (email and maps), Anthropic (the assistant that helps answer texts) and the
              company that hosts our servers.
            </li>
            <li>
              <b>When the law requires it.</b>
            </li>
          </ul>
          <p>
            We do not sell your personal information. We never use bank data for marketing. We never share your consent to get
            texts with anyone for their own marketing, so a text about our services comes only from the brand you gave your number
            to.
          </p>
        </Section>
        <Section title="Your choices">
          <ul>
            <li>
              <b>Texts.</b> Reply STOP to any text from us and we stop texting you. We keep a record of when you said yes or stop.
            </li>
            <li>
              <b>Offers from our other brands.</b> Email us and we stop marketing to you across all of our brands. You still get
              messages about work you booked, like reminders and invoices.
            </li>
            <li>
              <b>Your information.</b> Ask us to see, correct or delete it.
            </li>
          </ul>
        </Section>
        <Section title="How we protect it">
          <p>
            Every connection uses HTTPS. Saved keys and bank data are encrypted. Each person has their own login and sees only
            what their job needs. Owner and office logins need a second code every time they sign in.
          </p>
        </Section>
        <Section title="How long we keep it">
          <p>
            Records are kept while they are needed to run the business and for 7 years after, for tax records, then deleted. We
            answer a request to see, correct or delete your information within 30 days unless the law requires us to keep it.
          </p>
        </Section>
        <Section title="Contact">
          <p>
            The Trades LLC, New Orleans, Louisiana. Email <a href="mailto:halfi@thecleanersusa.com">halfi@thecleanersusa.com</a>.
          </p>
        </Section>
      </main>
      <div className="public-band" />
    </div>
  );
}
